import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { HMITemplate, HMIRenderer, createHmiConfiguration, updateHmiConfiguration } from 'src/hmi';
import HmiLayoutBuilder from 'src/hmi/components/builder/HmiLayoutBuilder';
import RulesPanel from 'src/hmi/components/builder/RulesPanel';
import ComputedFieldsPanel from 'src/hmi/components/builder/ComputedFieldsPanel';

export interface HmiTemplateEditorModalProps {
  template: HMITemplate;
  readOnly?: boolean;
  /** Whether this twin has no configuration yet (POST) vs. replacing its existing one (PUT). */
  isNew: boolean;
  solutionId: string;
  siteId: string;
  twinId: string;
  onClose: () => void;
  onSaved: () => void;
}

type EditorTab = 'layout' | 'rules' | 'computed' | 'json';

/**
 * Visual builder + advanced JSON fallback for a `HMITemplate`, with a
 * "Preview" button that opens the live render in its own modal.
 *
 * - "Layout" tab: define tabs (screens), nest subtabs under any tab, add/size
 *   sections (groups) on a 12-column grid, and add/edit/delete each
 *   section's fields and widgets/graphs - no JSON required.
 * - "Rules" tab: cross-field dependency/validation rules (value comparisons
 *   between fields, mutual exclusivity, conditional constraints) - see
 *   `HMIFieldRule`.
 * - "Computed Fields" tab: fields whose value is calculated in the UI from
 *   other fields (e.g. a grid aggregate times another field) instead of
 *   coming from the recipe file or device - see `HMIComputedFormula`.
 * - "Advanced (JSON)" tab: raw `HMITemplate` document, for bulk edits or
 *   properties not yet exposed by the visual builder.
 *
 * The preview renders the exact same `HMIRenderer` component used in the
 * Digital Twin > HMI tab, so "what you configure is what you get" - a key
 * property of a server-driven UI. It used to sit in a permanent side panel;
 * it's now a separate modal (opened on demand via "Preview") so the builder
 * gets the full dialog width to work with.
 */
function HmiTemplateEditorModal({
  template: initialTemplate,
  readOnly,
  isNew,
  solutionId,
  siteId,
  twinId,
  onClose,
  onSaved,
}: HmiTemplateEditorModalProps) {
  const [template, setTemplate] = useState<HMITemplate>(initialTemplate);
  const [activeTab, setActiveTab] = useState<EditorTab>('layout');
  const [json, setJson] = useState(JSON.stringify(initialTemplate, null, 2));
  const [jsonError, setJsonError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleJsonChange = (value: string) => {
    setJson(value);
    try {
      const candidate = JSON.parse(value);
      if (!Array.isArray(candidate.fields) || !Array.isArray(candidate.screens)) {
        setJsonError('Missing required "screens"/"fields" arrays.');
        return;
      }
      setJsonError(undefined);
      // Merge onto the current template rather than replacing it outright:
      // pasting a fragment that only has "screens"/"groups"/"fields" (e.g.
      // `defaultFieldsData.json`) must not wipe out `id`/`name`/`version`/
      // etc. `id` in particular must never change here - the host page
      // (e.g. the per-twin "HMI Configuration" tab) already captured it
      // before this modal opened and uses it to (re)assign the template to
      // a twin after save, so losing it silently orphans the save.
      setTemplate({ ...template, ...candidate, id: template.id } as HMITemplate);
    } catch {
      setJsonError('JSON is invalid.');
    }
  };

  const handleTabChange = (next: EditorTab) => {
    if (next === 'json') {
      // Re-sync the JSON draft with whatever the visual builder produced.
      setJson(JSON.stringify(template, null, 2));
      setJsonError(undefined);
    }
    setActiveTab(next);
  };

  const previewTemplate = useMemo(() => template, [template]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      if (isNew) {
        await createHmiConfiguration(solutionId, siteId, twinId, template);
      } else {
        // `initialTemplate.id` is the backend-assigned `hmiConfigurationId`
        // this modal was opened to edit; it's never reassigned while
        // editing (see `handleJsonChange`'s id-preserving merge above).
        await updateHmiConfiguration(solutionId, siteId, twinId, initialTemplate.id, template);
      }
      onSaved();
    } catch {
      setSaveError('Failed to save the HMI configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="xl" onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        Edit HMI Template
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Name"
            size="small"
            value={template.name}
            disabled={readOnly}
            onChange={(e) => setTemplate({ ...template, name: e.target.value })}
            sx={{ flex: '1 1 260px' }}
          />
          <TextField
            label="Description"
            size="small"
            value={template.description ?? ''}
            disabled={readOnly}
            onChange={(e) => setTemplate({ ...template, description: e.target.value })}
            sx={{ flex: '2 1 360px' }}
          />
          <TextField
            label="Poll interval (s)"
            size="small"
            type="number"
            value={template.pollIntervalSec}
            disabled={readOnly}
            onChange={(e) =>
              setTemplate({ ...template, pollIntervalSec: Number(e.target.value) || 0 })
            }
            sx={{ width: 160 }}
          />
        </Stack>

        <Box sx={{ height: '65vh', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => handleTabChange(v)}
              sx={{ minHeight: 36 }}
            >
              <Tab value="layout" label="Layout" sx={{ minHeight: 36 }} />
              <Tab value="rules" label="Rules" sx={{ minHeight: 36 }} />
              <Tab value="computed" label="Computed Fields" sx={{ minHeight: 36 }} />
              <Tab value="json" label="Advanced (JSON)" sx={{ minHeight: 36 }} />
            </Tabs>
            <Button
              size="small"
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => setPreviewOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Preview
            </Button>
          </Stack>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: 1 }}>
            {activeTab === 'layout' && (
              <HmiLayoutBuilder
                template={template}
                readOnly={readOnly}
                onChange={setTemplate}
              />
            )}
            {activeTab === 'rules' && (
              <RulesPanel template={template} readOnly={readOnly} onChange={setTemplate} />
            )}
            {activeTab === 'computed' && (
              <ComputedFieldsPanel template={template} readOnly={readOnly} onChange={setTemplate} />
            )}
            {activeTab === 'json' && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Full HMITemplate document, for bulk changes or properties not yet exposed
                  by the visual builder. Screens/groups/fields edited here also drive the
                  Layout tab.
                </Typography>
                <textarea
                  value={json}
                  disabled={readOnly}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: '100%',
                    height: 'calc(100% - 28px)',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    whiteSpace: 'pre',
                    resize: 'none',
                    padding: 8,
                    boxSizing: 'border-box',
                  }}
                />
                {jsonError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {jsonError}
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {saveError && (
            <Alert severity="error" sx={{ py: 0 }}>
              {saveError}
            </Alert>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={readOnly || saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </DialogActions>

      {previewOpen && (
        <Dialog open fullWidth maxWidth="lg" onClose={() => setPreviewOpen(false)}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
            Live preview
            <IconButton size="small" onClick={() => setPreviewOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: '75vh' }}>
            <HMIRenderer template={previewTemplate} twinId="preview-twin" showAllScreens />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}

export default HmiTemplateEditorModal;

